import { FilterXSS, safeAttrValue as valorSeguroPorDefecto } from 'xss';

/**
 * Limpieza del HTML del Rich Text del Builder, del lado del sitio publicado.
 *
 * ## Por qué existe
 *
 * El contenido de los textos y títulos se guarda como HTML: es el `innerHTML`
 * del `contentEditable` donde el dealer escribe, y acá se pinta con
 * `dangerouslySetInnerHTML`. Escribir a mano produce markup inofensivo, pero
 * PEGAR no: un pegado trae el HTML de donde venga, y ahí puede entrar un
 * `<img onerror>` o un `<iframe>`.
 *
 * Esta es la segunda línea de defensa: el editor ya limpia al guardar, pero lo
 * que está guardado se escribió sin ese filtro y nadie va a migrar las páginas
 * de todos los clientes.
 *
 * ## Por qué NO DOMPurify acá
 *
 * DOMPurify necesita un DOM. Todo este árbol cuelga de `BuilderPageWrapper`,
 * que es `'use client'`, así que corre en el navegador Y en el pre-render del
 * servidor. Resolverlo con `isomorphic-dompurify` metía jsdom en ese grafo: en
 * local andaba, en Vercel tumbó TODAS las rutas del Builder de TODOS los
 * tenants con 500. Las dos que no importan este árbol —/vehicles y /embed—
 * quedaron en pie, que fue justo la pista.
 *
 * `xss` es JavaScript puro: mismo código y misma salida en el servidor y en el
 * navegador, sin DOM, sin dependencias nativas y sin nada que el tracer de
 * Vercel pueda dejarse afuera. Que la salida sea idéntica en los dos lados no
 * es un detalle: si difiriera, React marcaría error de hidratación en cada
 * texto de cada página.
 *
 * ## Por qué no un filtro propio
 *
 * Porque un sanitizador con expresiones regulares se rompe con el primer
 * `<img src=x onerror=alert(1)//` sin comillas, con entidades HTML o con
 * cualquiera de las decenas de variantes que una librería mantenida ya tiene
 * cubiertas. Acá se parsea el HTML de verdad y se decide etiqueta por etiqueta.
 *
 * La POLÍTICA (las tres listas de abajo) es la misma que aplica el editor en
 * goautos-admin, que sí corre solo en el navegador y usa DOMPurify. Son dos
 * implementaciones porque los runtimes son distintos; las listas tienen que
 * seguir diciendo lo mismo. `test:builder-parity:ci` compara los dos renders.
 */

/**
 * Las etiquetas que el editor produce, y nada más.
 *
 * `b/i/u` las escribe `execCommand`; `strong/em/s` pueden llegar de un pegado
 * legítimo y significan lo mismo. `span` es el caballito de batalla de la barra
 * —color, tamaño, tipografía y alineación—, `font` aparece en contenido viejo,
 * y `div/p/br` son lo que deja el Enter según el navegador.
 */
export const ETIQUETAS_PERMITIDAS = [
  'b', 'strong', 'i', 'em', 'u', 's', 'br', 'span', 'font', 'div', 'p', 'a',
];

/**
 * `data-alinear` es la marca de la envoltura de alineación de la barra; sin ella
 * la barra no sabe qué botón marcar. Los de `font` son para el contenido viejo.
 *
 * `rel` no está y es a propósito: lo ponemos nosotros en los enlaces que abren
 * otra pestaña (ver `onTagAttr`). Si además se aceptara del contenido, un
 * enlace podría traer el suyo y quedarían dos `rel` en la misma etiqueta —
 * gana el primero, y cuál es el primero depende de cómo venga escrito.
 */
export const ATRIBUTOS_PERMITIDOS = [
  'style', 'data-alinear', 'color', 'size', 'face', 'href', 'target',
];

/**
 * Propiedades CSS que sobreviven. Son EXACTAMENTE las que escribe la barra más
 * las que puede traer contenido viejo equivalente.
 *
 * No se acepta CSS libre: un `style` sin filtrar permite `position: fixed` sobre
 * toda la pantalla, o un `background: url(...)` que filtra la IP de quien mira.
 */
export const ESTILOS_PERMITIDOS = [
  'color',
  'background-color',
  'font-size',
  'font-family',
  'font-weight',
  'font-style',
  'text-decoration',
  'text-decoration-line',
  'text-align',
  'display',
  'line-height',
  'letter-spacing',
];

/** Valores que no se aceptan en ninguna propiedad, valga la que valga. */
const VALOR_PELIGROSO = /url\s*\(|expression\s*\(|javascript:|@import|<|\/\*/i;

/** `display` solo puede ser lo que usa la envoltura de alineación. */
const DISPLAY_PERMITIDO = ['block', 'inline', 'inline-block'];

/** Sin valor propio: aparecen cuando el navegador expande una propiedad compuesta. */
const SIN_VALOR = ['initial', 'inherit', 'unset', 'revert'];

const estiloAceptable = (propiedad: string, valor: string): boolean => {
  const v = String(valor).trim();
  if (!v || VALOR_PELIGROSO.test(v) || SIN_VALOR.includes(v.toLowerCase())) return false;
  if (propiedad === 'display') return DISPLAY_PERMITIDO.includes(v.toLowerCase());
  return true;
};

/** La lista de CSS en el formato que espera el filtro: propiedad → validador. */
const CSS_PERMITIDO = Object.fromEntries(
  ESTILOS_PERMITIDOS.map((p) => [p, (valor: string) => estiloAceptable(p, valor)]),
);

/** Los únicos esquemas que puede tener un enlace del contenido. */
const ESQUEMAS_PERMITIDOS = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Un enlace pasa si es relativo o si su esquema está permitido.
 *
 * Antes de mirarlo se le sacan espacios y caracteres de control: por ahí se
 * cuelan los `java&#9;script:` de manual, que el navegador vuelve a juntar.
 */
const enlaceSeguro = (valor: string): string => {
  const plano = String(valor).replace(/[\u0000-\u0020\u007f-\u00a0\ufeff]/g, '').toLowerCase();
  const esquema = plano.match(/^([a-z][a-z0-9+.-]*):/);
  if (!esquema) return valor; // relativo, ancla o consulta: no navega a otro protocolo
  return ESQUEMAS_PERMITIDOS.includes(esquema[1] + ':') ? valor : '';
};

/**
 * Escapa los `<` que no abren una etiqueta.
 *
 * Un título que dice "Autos < 10.000" es texto, no markup, y el parser del
 * navegador lo entiende así: un `<` solo empieza una etiqueta si lo sigue una
 * letra, `/`, `!` o `?`. El filtro, en cambio, se comía todo lo que venía
 * después. Esto NO es la sanitización —de eso se encarga la librería— sino
 * ponerle delante la misma regla que aplica el navegador, que además es la que
 * ya vio el editor al guardar. Escapar de más nunca abre un agujero.
 */
const escaparMenorSuelto = (html: string): string => html.replace(/<(?![a-zA-Z!/?])/g, '&lt;');

/** Las etiquetas permitidas aceptan todas las de la lista: misma política que el editor. */
const LISTA_BLANCA = Object.fromEntries(
  ETIQUETAS_PERMITIDAS.map((etiqueta) => [etiqueta, [...ATRIBUTOS_PERMITIDOS]]),
);

const filtro = new FilterXSS({
  whiteList: LISTA_BLANCA,
  css: { whiteList: CSS_PERMITIDO },
  // Las etiquetas que no están en la lista se van, pero su texto se queda: un
  // `<h1>Hola</h1>` pegado tiene que seguir diciendo Hola.
  stripIgnoreTag: true,
  // Salvo estas, donde el contenido ES el peligro y no hay texto que rescatar.
  stripIgnoreTagBody: ['script', 'style', 'template', 'iframe', 'object', 'embed'],
  safeAttrValue(etiqueta, nombre, valor, filtroCss) {
    if (nombre === 'href') return enlaceSeguro(valor);
    return valorSeguroPorDefecto(etiqueta, nombre, valor, filtroCss);
  },
  onTagAttr(etiqueta, nombre, valor, esPermitido) {
    // Un enlace que abre en otra pestaña sin `noopener` le da a la página
    // destino acceso a `window.opener`. El `rel` lo ponemos acá y no se acepta
    // del contenido, así que no puede haber dos.
    if (esPermitido && etiqueta === 'a' && nombre === 'target') {
      return `target="${String(valor).replace(/"/g, '')}" rel="noopener noreferrer"`;
    }
    return undefined;
  },
});

/**
 * Limpia el HTML de un texto del Builder.
 *
 * Vacío o nulo devuelven cadena vacía: es lo que el resto del código ya espera
 * para "campo sin contenido".
 */
export const limpiarHtml = (html?: string | null): string => {
  if (html == null || html === '') return '';
  return filtro.process(escaparMenorSuelto(String(html)));
};

/**
 * Lo que se le pasa a `dangerouslySetInnerHTML`.
 *
 * Se usa en TODOS los lugares donde el Builder pinta HTML guardado.
 */
export const htmlSeguro = (html?: string | null): { __html: string } => ({
  __html: limpiarHtml(html),
});
